// The agent-chat welcome entry state: mascot + beta tag + a prompt and a row of
// starter "pills" that seed the first turn. Rebuilt on the design-system Empty
// composition + Button (W-24314366), replacing the retired
// tds2_shim/agentforce/WelcomeState. Panel-specific layout (full-panel fill,
// mascot size, the horizontal pills row) lives in `.tds2-agent-entry-state` in
// _agent_chat.scss.
import React from 'react';
import PropTypes from 'prop-types';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription, EmptyContent, Button } from '@trailhead-emu/design-system';
import BetaTag from 'components/agentforce/BetaTag';

function PillIcon({ src }) {
  if (!src) return null;
  return <img src={src} alt="" className="tds2-agent-entry-state__pill-icon" aria-hidden="true" />;
}

PillIcon.propTypes = { src: PropTypes.string };

// The welcome screen renders a list of starter "pills". The unit owns this list
// (via agent_chat.welcome → ChatContainer), so the persona — one "Help Agent"
// vs. the legacy Sales/Service tracks — is curriculum content, not code.
//
// `pills` is the canonical input: an array of { label, prompt, variant, icon }
// (onClick is wired by the caller). When `pills` is not provided at all (the
// `null` default — no caller passed the prop), we fall back to the legacy
// Service / Sales / Help-Me-Choose pills built from the discrete callback
// props, so existing callers keep working unchanged. A caller that DOES pass
// an array — even `[]` — means it, deliberately: an empty array renders zero
// pills rather than resurfacing the legacy defaults, so a unit can configure a
// title/subtitle with intentionally no starter pills.
function legacyPills({
  servicePillIconUrl,
  salesPillIconUrl,
  onSelectService,
  onSelectSales,
  onHelpMeChoose,
}) {
  const pills = [
    {
      key: 'service',
      label: 'Service',
      variant: 'secondary',
      icon: servicePillIconUrl,
      onClick: onSelectService,
    },
    {
      key: 'sales',
      label: 'Sales',
      variant: 'secondary',
      icon: salesPillIconUrl,
      onClick: onSelectSales,
    },
  ];

  if (onHelpMeChoose) {
    pills.push({
      key: 'help',
      label: 'Help Me Choose',
      variant: 'tertiary',
      onClick: onHelpMeChoose,
    });
  }

  return pills;
}

export default function WelcomeState({
  mascotUrl,
  servicePillIconUrl,
  salesPillIconUrl,
  title = 'Build an agent through conversation.',
  subtitle = 'Ready to build? Describe your Help Agent below to get started using ' +
    'natural language.',
  pills = null,
  onSelectService,
  onSelectSales,
  onHelpMeChoose,
}) {
  const resolvedPills = Array.isArray(pills)
    ? pills.map((pill, i) => ({ key: pill.key || `pill-${i}`, ...pill }))
    : legacyPills({
        servicePillIconUrl,
        salesPillIconUrl,
        onSelectService,
        onSelectSales,
        onHelpMeChoose,
      });

  return (
    <Empty variant="flush" className="tds2-agent-entry-state">
      <EmptyHeader>
        {mascotUrl ? (
          <EmptyMedia variant="img">
            <img src={mascotUrl} alt="" aria-hidden="true" />
          </EmptyMedia>
        ) : null}
        <BetaTag />
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{subtitle}</EmptyDescription>
      </EmptyHeader>
      {resolvedPills.length > 0 && (
        <EmptyContent>
          <div
            className="tds2-agent-entry-state__pills"
            role="group"
            aria-label="Choose a starter scenario"
          >
            {resolvedPills.map((pill) => (
              <Button
                key={pill.key}
                variant={pill.variant || 'secondary'}
                size="small"
                iconStart={pill.icon ? <PillIcon src={pill.icon} /> : null}
                onClick={pill.onClick}
              >
                {pill.label}
              </Button>
            ))}
          </div>
        </EmptyContent>
      )}
    </Empty>
  );
}

WelcomeState.propTypes = {
  mascotUrl: PropTypes.string,
  servicePillIconUrl: PropTypes.string,
  salesPillIconUrl: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  // Unit-configured starter pills. Each: { label, prompt(optional, used by the
  // caller's onClick), variant, icon, onClick, key }. When present, replaces the
  // legacy Service/Sales/Help-Me-Choose pills entirely.
  pills: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      variant: PropTypes.string,
      icon: PropTypes.string,
      onClick: PropTypes.func,
    })
  ),
  onSelectService: PropTypes.func,
  onSelectSales: PropTypes.func,
  onHelpMeChoose: PropTypes.func,
};
